// Shared "Events" tab for the four entity detail pages. Splits the
// viewport into a scrolling event list on the left and a MapView on the
// right (the same MapView the global Timeline view uses), scoped to the
// events that actually belong to this entity.
//
// Clicking a list row focuses that event on the map (when the map is
// visible); on narrow viewports where the map is hidden, clicking opens
// the View Event dialog directly. Map markers always open View Event.

import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setDialogOpen, setSelectedEvent } from '../../store/Actions';
import { MapView } from '../mapView/MapView';
import { getEventStartAt } from './insightHelpers';

const SHORT_MONTH = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const formatEventDate = (date) => {
  if(!date) return '';
  return `${SHORT_MONTH[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

const formatEventTime = (date) => {
  if(!date) return '';
  // Treat midnight as "no time" since startTime defaults to 00:00 when
  // the event was added without an explicit time.
  if(date.getHours() === 0 && date.getMinutes() === 0) return '';
  return date.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'});
};

const sortNewestFirst = (eventsWithDates) => {
  return [...eventsWithDates].sort((a, b) => b.startAt - a.startAt);
};

function CompactEvent({event, startAt, category, trips, friends, tags, isActive, onClick}) {
  const tripNames = (event.tripIds || []).map(id => trips?.[id]?.name).filter(Boolean);
  const friendNames = (event.friendIds || []).map(id => friends?.[id]?.name).filter(Boolean);
  const tagNames = (event.tagIds || []).map(id => tags?.[id]?.name).filter(Boolean);
  const metadata = [
    category?.name,
    tripNames.length > 0 ? tripNames.join(', ') : '',
    friendNames.length > 0 ? friendNames.join(', ') : '',
    tagNames.length > 0 ? tagNames.join(', ') : '',
  ].filter(Boolean);
  const eventTime = formatEventTime(startAt);

  return (
    <button
      type="button"
      className={`detail-view__compact-event ${isActive ? 'is-active' : ''}`.trim()}
      onClick={onClick}
    >
      <div className="detail-view__compact-event__date">
        <span>{formatEventDate(startAt)}</span>
        {eventTime &&
          <span className="detail-view__compact-event__time">{eventTime}</span>
        }
      </div>
      <div
        className={`detail-view__compact-event__cover ${event.coverPicUrl ? 'has-image' : ''}`.trim()}
        style={{backgroundColor: category?.pinColor || '#e5e7eb'}}
      >
        {event.coverPicUrl &&
          <img src={event.coverPicUrl} alt="" loading="lazy"/>
        }
        {!event.coverPicUrl &&
          <span className="detail-view__compact-event__marker">
            {event.emoji || category?.defaultEmoji}
          </span>
        }
      </div>
      <div className="detail-view__compact-event__body">
        <div className="detail-view__compact-event__title">{event.title || 'Untitled event'}</div>
        {event.location?.description &&
          <div className="detail-view__compact-event__location">
            {event.location.description}
          </div>
        }
        {metadata.length > 0 &&
          <div className="detail-view__compact-event__meta">{metadata.join(' · ')}</div>
        }
      </div>
    </button>
  );
}

export function EntityEvents({events: scopedEvents, emptyText = 'No events yet'}) {
  const categories = useSelector(state => state.viewingUserCategories);
  const trips = useSelector(state => state.viewingUserTrips);
  const friends = useSelector(state => state.viewingUserFriends);
  const tags = useSelector(state => state.viewingUserTags);
  const selectedEvent = useSelector(state => state.selectedEvent);
  const appWidth = useSelector(state => state.appWidth);
  const mapVisible = appWidth > 980;
  const dispatch = useDispatch();

  // Pair every event with its parsed start date once so sorting + the
  // compact rows don't have to recompute it. Events with no start date
  // (extremely rare in practice) are still listed but pushed to the end.
  const eventsWithDates = useMemo(() => {
    return (scopedEvents || []).map(event => ({event, startAt: getEventStartAt(event)}));
  }, [scopedEvents]);

  const sortedEvents = useMemo(() => sortNewestFirst(
    eventsWithDates.filter(item => item.startAt)
  ), [eventsWithDates]);
  const undatedEvents = useMemo(() => eventsWithDates.filter(item => !item.startAt), [eventsWithDates]);
  const allEvents = [...sortedEvents, ...undatedEvents];

  const openEventDetail = (event) => {
    dispatch(setSelectedEvent(event));
    dispatch(setDialogOpen('View Event', {event}));
  };

  const focusEventOnMap = (event) => {
    dispatch(setSelectedEvent(event));
  };

  // First click focuses the event on the map; clicking the already-selected
  // event (or any click when the map is hidden) opens the View Event dialog.
  const handleListEventClick = (event) => {
    if(!mapVisible || selectedEvent?.id === event.id){
      openEventDetail(event);
    } else {
      focusEventOnMap(event);
    }
  };

  // Marker clicks always open the View Event dialog — the map doesn't
  // need a "selected" hover state when the user wants the full record.
  const handleMarkerClick = openEventDetail;

  // Pass only this entity's events to MapView so unrelated markers
  // don't show up. We sort the override copy newest-first to match
  // the visible list order and avoid surprises in clusters.
  const mapEventsOverride = useMemo(() => allEvents.map(item => item.event), [allEvents]);

  return (
    <div className="detail-view__events-pane">
      <div className="detail-view__events-pane-list">
        {allEvents.length === 0 &&
          <div className="detail-view__events-pane-empty">{emptyText}</div>
        }
        {allEvents.map(({event, startAt}) => (
          <CompactEvent
            key={event.id}
            event={event}
            startAt={startAt}
            category={categories?.[event.categoryId]}
            trips={trips}
            friends={friends}
            tags={tags}
            isActive={selectedEvent?.id === event.id}
            onClick={() => handleListEventClick(event)}
          />
        ))}
      </div>
      <aside className="detail-view__events-pane-map">
        <MapView
          hideSidePanel={true}
          hideAddEvent={true}
          eventsOverride={mapEventsOverride}
          onMarkerClick={handleMarkerClick}
        />
      </aside>
    </div>
  );
}
